import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { UserLinks } from '../../../components/UserLinks';

export interface LeaderboardUser {
    userId: string;
    email: string | null;
    displayName: string | null;
    value: number;
    secondaryValue?: string;
    memberSince: Date;
    lastUsedAt?: Date | null;
}

interface StatsLeaderboardProps {
    title: string;
    valueLabel: string;
    valueUnit: string;
    secondaryValueLabel?: string;
    emptyMessage: string;
    fetchUsers: () => Promise<LeaderboardUser[]>;
    showLastUsed?: boolean;
}

const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatDateTime = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const StatsLeaderboard = ({
    title,
    valueLabel,
    valueUnit,
    secondaryValueLabel,
    emptyMessage,
    fetchUsers,
    showLastUsed = false,
}: StatsLeaderboardProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [users, setUsers] = useState<LeaderboardUser[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleFetch = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await fetchUsers();
            setUsers(result);
        } catch (err) {
            console.error(`Error fetching ${title}:`, err);
            setError(err instanceof Error ? err.message : 'Failed to fetch users');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleFetch();
    }, []);

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading {title.toLowerCase()}...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Button onClick={handleFetch} variant="primary">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                <Button onClick={handleFetch} variant="primary">
                    Refresh
                </Button>
            </div>

            {users.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    {emptyMessage}
                </div>
            ) : (
                <>
                    <div className="mb-4 text-sm text-gray-600">
                        Showing top {users.length} users by {valueLabel.toLowerCase()}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rank
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {valueLabel}
                                    </th>
                                    {secondaryValueLabel && (
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {secondaryValueLabel}
                                        </th>
                                    )}
                                    {showLastUsed && (
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Used
                                        </th>
                                    )}
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Member Since
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((user, index) => (
                                    <tr
                                        key={user.userId}
                                        className={index < 3 ? 'bg-yellow-50' : ''}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <span className={`text-sm font-bold ${
                                                    index === 0 ? 'text-yellow-600 text-lg' :
                                                    index === 1 ? 'text-gray-500 text-lg' :
                                                    index === 2 ? 'text-orange-600 text-lg' :
                                                    'text-gray-900'
                                                }`}>
                                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {user.email || 'No email'}
                                            </div>
                                            {user.displayName && (
                                                <div className="text-sm text-gray-500">
                                                    {user.displayName}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-purple-600">
                                                {user.value} {valueUnit}
                                            </div>
                                        </td>
                                        {secondaryValueLabel && (
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-blue-600">
                                                    {user.secondaryValue ?? '-'}
                                                </div>
                                            </td>
                                        )}
                                        {showLastUsed && (
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.lastUsedAt ? formatDateTime(user.lastUsedAt) : '-'}
                                            </td>
                                        )}
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(user.memberSince)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <UserLinks userId={user.userId} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};
